import React from "react";
import {
    Box, Button, Checkbox, Chip, IconButton,
    Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
    Typography, Select, MenuItem, FormControl, useMediaQuery, useTheme, Fab,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Menu, Plus } from "lucide-react";

import DynamicTableVirtual from "../components/table-virtual";
import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import SectionWrapper from "../components/section";
import AddUserPopup from "../components/popup/add-user";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";

import type { Column } from '../components/table-virtual';
import AddBtn from "../components/ui/add-btn";
import securityImage from '../files/images/user.png';
import accessImage from '../files/images/access.png';

import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { useSettings } from "../providers/settings-provider";
import { usePermissions } from "../providers/permissions-provider";
import { usePlanFeature } from "../hooks/use-plan-feature";
import PlanUpgradeBar from "../components/ui/plan-upgrade-bar";

type User = {
    id: number;
    name: string;
    role: string;
    status: string;
    entityRoleId: number | null;
    invitedEmail: string;
};

interface SubAction { key: string; label: string }
// Not every resource supports every CRUD action (e.g. singleton settings pages
// can't be "created" or "deleted") - hiddenActions keeps those columns out of
// the matrix for that row instead of showing a checkbox that can never mean
// anything.
// subActionColumn: which column a row's sub-action checkboxes sit in (and the
// permission flag they're stored as) - Create by default; Integrations'
// Connect/Disconnect and Sync change an existing setup, so they're Update.
interface ResourceItem { key: string; label: string; subActions?: SubAction[]; hiddenActions?: Array<typeof ACTIONS[number]>; subActionColumn?: typeof ACTIONS[number] }
interface ResourceSection { section: string; items: ResourceItem[] }
type ResourceEntry = ResourceItem | ResourceSection;

const isSection = (r: ResourceEntry): r is ResourceSection => 'items' in r;

// Mirrors the sidebar nav structure (files/json/sidebar-menu.json) - same order,
// same grouping - so the permission matrix reads the same way the menu does.
const RESOURCES: ResourceEntry[] = [
    {
        key: 'transactions', label: 'Transactions', subActions: [
            { key: 'transactions.import', label: 'Import' },
            { key: 'transactions.addBankAccount', label: 'Add Bank Account' },
            { key: 'transactions.addProvision', label: 'Add Provision' },
        ]
    },
    { key: 'provisions', label: 'Provisions' },
    {
        key: 'invoices', label: 'Invoices', subActions: [
            { key: 'invoices.addClient', label: 'Add Client' },
            { key: 'invoices.import', label: 'Import' },
        ]
    },
    { key: 'staff', label: 'Staff Payroll' },
    { key: 'documents', label: 'Documents' },
    {
        section: 'Summaries', items: [
            { key: 'summaries_assumptions', label: 'Assumptions' },
            { key: 'summaries_profit_loss', label: 'Profit & Loss' },
            { key: 'summaries_forecasts', label: 'Forecasts' },
            { key: 'summaries_forecast_vs_actual', label: 'Forecast vs Actual' },
            // Nothing to create or delete on the GST summary.
            { key: 'summaries_gst', label: 'GST', hiddenActions: ['canCreate', 'canDelete'] },
        ]
    },
    {
        section: 'Settings', items: [
            { key: 'settings_company', label: 'Company', hiddenActions: ['canCreate', 'canDelete'] },
            { key: 'bank_accounts', label: 'Bank Accounts' },
            { key: 'settings_invoice', label: 'Invoice' },
            {
                key: 'categories', label: 'Categories', subActions: [
                    { key: 'categories.import', label: 'Import' },
                ]
            },
            { key: 'tax_definitions', label: 'Tax Definitions' },
            { key: 'clients', label: 'Clients' },
            { key: 'staff_payment', label: 'Staff Payment' },
            { key: 'imports', label: 'Imports', hiddenActions: ['canCreate', 'canDelete'] },
            {
                key: 'integrations', label: 'Integrations', hiddenActions: ['canCreate', 'canDelete'], subActionColumn: 'canUpdate', subActions: [
                    { key: 'integrations.connect', label: 'Connect/Disconnect' },
                    { key: 'integrations.sync', label: 'Sync' },
                ]
            },
            { key: 'settings_security', label: 'User Security' },
        ]
    },
];

// Flat list of every resource item, section children included - used for
// key/permission bookkeeping where the grouping doesn't matter.
const ALL_ITEMS: ResourceItem[] = RESOURCES.flatMap(r => isSection(r) ? r.items : [r]);

// Every resource + sub-action key, in display order - used to make sure a role's
// permission set always has an entry for every row, even ones added after the
// role was first created.
const ALL_PERMISSION_KEYS: string[] = ALL_ITEMS.flatMap(r => [r.key, ...(r.subActions?.map(s => s.key) ?? [])]);

const ACTIONS = ['canRead', 'canCreate', 'canUpdate', 'canDelete'] as const;
const ACTION_LABELS: Record<typeof ACTIONS[number], string> = {
    canRead: 'Read', canCreate: 'Create', canUpdate: 'Update', canDelete: 'Delete',
};

const defaultPerm = (resource: string): Permission => (
    { resource, canRead: true, canCreate: false, canUpdate: false, canDelete: false }
);

// Fills in a missing entry (with safe defaults) for any resource/sub-action key
// that isn't present yet in a role's saved permissions - e.g. a role saved
// before a new page or sub-action was added.
const normalizePerms = (permissions: Permission[]): Permission[] =>
    ALL_PERMISSION_KEYS.map(key => permissions.find(p => p.resource === key) ?? defaultPerm(key));

interface Permission {
    resource: string;
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
}

interface Role {
    id: number;
    name: string;
    permissions: Permission[];
    isSystem?: boolean;
}

// "Admin" and "Read Only" mirror the built-in EntityUser.role toggle - they're not
// real EntityRole rows, just a fixed, non-editable reference shown alongside custom
// roles so the full permission picture (system + custom) lives in one place.
const SYSTEM_ROLES: Role[] = [
    {
        id: -1,
        name: 'Admin',
        isSystem: true,
        permissions: ALL_PERMISSION_KEYS.map(key => ({ resource: key, canRead: true, canCreate: true, canUpdate: true, canDelete: true })),
    },
    {
        id: -2,
        name: 'Read Only',
        isSystem: true,
        permissions: ALL_PERMISSION_KEYS.map(key => ({ resource: key, canRead: true, canCreate: false, canUpdate: false, canDelete: false })),
    },
];

const Security: React.FC = () => {
    const { user } = useUser();
    const { has: hasUserAcl, prompt: promptUserAcl } = usePlanFeature('userAcl');
    const { formatDate } = useSettings();
    const { showSuccess, showError } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    // Server-verified per-entity admin flag, not User.role from the login
    // cookie - that field is set to 'admin' for every signed-up account
    // regardless of their actual EntityUser role within any given entity, so
    // it can't distinguish a real admin from a read-only user here.
    const { isAdmin, can, reload: reloadPermissions } = usePermissions();
    // This page follows its own User Security permissions (admins pass all of
    // them) - it used to be admin-only regardless, so a custom role granted
    // Create/Update/Delete here still had everything disabled. Making someone
    // Admin (or changing an Admin) stays admin-only, so a role can't promote
    // itself.
    const canCreateSecurity = can('settings_security', 'create');
    const canUpdateSecurity = can('settings_security', 'update');
    const canDeleteSecurity = can('settings_security', 'delete');
    // Nobody changes their own role - admins included (enforced server-side
    // too). Rows are matched to the signed-in user by email.
    const isSelf = (row: { invitedEmail?: string }) =>
        !!row.invitedEmail && !!user?.email && row.invitedEmail.toLowerCase() === user.email.toLowerCase();
    const [data, setData] = React.useState<User[]>([]);
    const list = useListSearch(data);
    const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [sortBy, setSortBy] = React.useState<string>('');
    const [sortDirection, setSortDirection] = React.useState<'ASC' | 'DESC'>('ASC');

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();

    // Roles & Permissions state
    const [roles, setRoles] = React.useState<Role[]>([]);
    const [selectedRole, setSelectedRole] = React.useState<Role | null>(null);
    const [pendingPerms, setPendingPerms] = React.useState<Permission[]>([]);
    const [newRoleName, setNewRoleName] = React.useState('');
    const [addingRole, setAddingRole] = React.useState(false);

    const [rolesResult, setRolesConfig] = useApi();
    const [createRoleResult, setCreateRoleConfig] = useApi();
    const [updateRoleResult, setUpdateRoleConfig] = useApi();
    const [deleteRoleResult, setDeleteRoleConfig] = useApi();
    const [permsResult, setPermsConfig] = useApi();
    const [assignResult, setAssignConfig] = useApi();
    // Set by handleCreate/handleUpdate when the Add/Edit User popup's role
    // dropdown resolves to a custom role - applied via a follow-up assignRole
    // call once we know the entityUserId (immediately for update, from the
    // create response's id for create).
    const pendingCustomRoleIdRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        document.title = "FineAnts | Security Settings";
    }, []);

    React.useEffect(() => {
        const params = [
            { param: 'entityId', value: user?.entityId },
            { param: 'sortBy', value: sortBy },
            { param: 'sortOrder', value: sortDirection },
        ];
        const { url, method } = buildApiRequestConfig(routes.userSettings.fetchByEntity, params);
        setFetch({ url, method });
        // assignResult too - a custom role picked in the Add/Edit User popup is
        // applied via a separate assignRole call after create/update succeeds,
        // so the list needs a refetch once that settles to show it.
    }, [user, sortBy, sortDirection, create, update, remove, assignResult, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (!isLoading && !error && data) {
            setData(data.map((item: any) => ({
                id: item.id,
                name: item.name,
                role: item.role === 'admin' ? 'Admin' : 'Read Only',
                status: item.status === 'invited' ? 'Pending Invitation' : `Accepted On: ${formatDate(new Date(item.status))}`,
                entityRoleId: item.entityRoleId ?? null,
                invitedEmail: item.invitedEmail,
            })));
        }
    }, [fetch, formatDate]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (!isLoading && data) {
            showSuccess('Invitation sent successfully');
            setOpenModal('');
            // Always sync, even to null - a freshly created user has no
            // entityRoleId yet, but this keeps create and update symmetric and
            // means a stray non-null ref value can never leak into the next save.
            if (data.id) {
                setAssignConfig({
                    ...buildApiRequestConfig(routes.entityRoles.assignRole, []),
                    body: { entityUserId: data.id, entityRoleId: pendingCustomRoleIdRef.current },
                });
            }
            pendingCustomRoleIdRef.current = null;
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to add user');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [create, showSuccess, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (!isLoading && data) {
            showSuccess('User updated successfully');
            setOpenModal('');
            // Always sync (see create effect above for why null is included too).
            if (selectedUserId) {
                setAssignConfig({
                    ...buildApiRequestConfig(routes.entityRoles.assignRole, []),
                    body: { entityUserId: selectedUserId, entityRoleId: pendingCustomRoleIdRef.current },
                });
            }
            pendingCustomRoleIdRef.current = null;
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to update user');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update, showSuccess, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (!isLoading && data) {
            showSuccess('User removed successfully');
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to remove user');
        }
    }, [remove, showSuccess, showError]);

    // Roles & Permissions effects
    React.useEffect(() => {
        if (!user?.entityId) return;
        setRolesConfig(buildApiRequestConfig(routes.entityRoles.fetchByEntity, [{ param: 'entityId', value: user.entityId }]));
    }, [user?.entityId, setRolesConfig]);

    React.useEffect(() => {
        const data = rolesResult.data;
        if (data?.success) {
            setRoles(data.roles);
            pickRole(SYSTEM_ROLES[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rolesResult.data]);

    React.useEffect(() => {
        const { data, error } = createRoleResult;
        if (data?.success) {
            showSuccess('Role created');
            setRoles(prev => [...prev, data.role]);
            pickRole(data.role);
            setNewRoleName('');
            setAddingRole(false);
        } else if (data && !data.success) {
            showError(data.error || 'Failed to create role');
        } else if (error) {
            showError(typeof error === 'string' ? error : 'Failed to create role');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createRoleResult.data, createRoleResult.error]);

    React.useEffect(() => {
        const { data, error } = updateRoleResult;
        if (data?.success) {
            showSuccess('Role updated');
            setRoles(prev => prev.map(r => r.id === data.role.id ? { ...r, ...data.role } : r));
            setSelectedRole(prev => prev?.id === data.role.id ? { ...prev, ...data.role } : prev);
        } else if (data && !data.success) {
            showError(data.error || 'Failed to update role');
        } else if (error) {
            showError(typeof error === 'string' ? error : 'Failed to update role');
        }
    }, [updateRoleResult.data, updateRoleResult.error, showSuccess, showError]);

    React.useEffect(() => {
        const { data, error } = deleteRoleResult;
        if (data?.success) {
            showSuccess('Role deleted');
            setRoles(prev => prev.filter(r => r.id !== selectedRole?.id));
            setSelectedRole(null);
            setPendingPerms([]);
        } else if (data && !data.success) {
            showError(data.error || 'Failed to delete role');
        } else if (error) {
            showError(typeof error === 'string' ? error : 'Failed to delete role');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteRoleResult.data, deleteRoleResult.error]);

    React.useEffect(() => {
        const { data, error } = permsResult;
        if (data?.success) {
            showSuccess('Permissions saved');
            reloadPermissions();
            if (selectedRole) {
                const updated = { ...selectedRole, permissions: data.permissions };
                setRoles(prev => prev.map(r => r.id === selectedRole.id ? updated : r));
                setSelectedRole(updated);
                setPendingPerms(normalizePerms(JSON.parse(JSON.stringify(data.permissions))));
            }
        } else if (data && !data.success) {
            showError(data.error || 'Failed to save permissions');
        } else if (error) {
            showError(typeof error === 'string' ? error : 'Failed to save permissions');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permsResult.data, permsResult.error]);

    React.useEffect(() => {
        const { data, error } = assignResult;
        if (data && !data.success) showError(data.error || 'Failed to assign role');
        else if (error) showError(typeof error === 'string' ? error : 'Failed to assign role');
    }, [assignResult.data, assignResult.error, showError]);

    // The Add/Edit User popup's role dropdown value is either 'admin', 'user',
    // or 'role:<entityRoleId>' for a custom role - decode into the base role
    // the backend's EntityUser.role field actually accepts, plus the optional
    // entityRoleId applied separately via assignRole once the user id is known.
    const decodeRoleSelection = (roleValue: string): { baseRole: 'admin' | 'user'; entityRoleId: number | null } =>
        roleValue.startsWith('role:')
            ? { baseRole: 'user', entityRoleId: Number(roleValue.slice(5)) }
            : { baseRole: roleValue === 'admin' ? 'admin' : 'user', entityRoleId: null };

    const handleCreate = (name: string, role: string, email: string) => {
        const { baseRole, entityRoleId } = decodeRoleSelection(role);
        pendingCustomRoleIdRef.current = entityRoleId;
        const { url, method } = buildApiRequestConfig(routes.userSettings.create);
        setCreate({ url, method, body: { name, role: baseRole, entityId: user?.entityId, invitedEmail: email } });
    };

    const handleUpdate = (name: string, role: string, email: string) => {
        const { baseRole, entityRoleId } = decodeRoleSelection(role);
        pendingCustomRoleIdRef.current = entityRoleId;
        const { url, method } = buildApiRequestConfig(routes.userSettings.update, [{ param: 'id', value: selectedUserId }]);
        setUpdate({ url, method, body: { name, role: baseRole, invitedEmail: email } });
    };

    const handleDelete = () => {
        const { url, method } = buildApiRequestConfig(routes.userSettings.delete, [{ param: 'id', value: selectedUserId }]);
        setRemove({ url, method });
    };

    const handleRoleChange = (userId: number, newRole: 'admin' | 'user') => {
        const { url, method } = buildApiRequestConfig(routes.userSettings.updateRole, [{ param: 'id', value: userId }]);
        setUpdate({ url, method, body: { role: newRole } });
    };

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            setSortDirection(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(columnId);
            setSortDirection('ASC');
        }
    };

    // Roles & Permissions handlers
    const pickRole = (role: Role) => {
        setSelectedRole(role);
        setPendingPerms(normalizePerms(JSON.parse(JSON.stringify(role.permissions))));
    };

    const handleCreateRole = () => {
        // Roles are User access control - read-only without it.
        if (!hasUserAcl) { promptUserAcl(); return; }
        if (!canCreateSecurity || !newRoleName.trim()) return;
        setCreateRoleConfig({
            ...buildApiRequestConfig(routes.entityRoles.create, []),
            body: { entityId: user?.entityId, name: newRoleName.trim() },
        });
    };

    const handleDeleteRole = (role: Role) => {
        // Roles are User access control - read-only without it.
        if (!hasUserAcl) { promptUserAcl(); return; }
        if (!canDeleteSecurity || role.isSystem) return;
        if (!window.confirm(`Delete role "${role.name}"? All users with this role will be unassigned.`)) return;
        setDeleteRoleConfig(buildApiRequestConfig(routes.entityRoles.delete, [{ param: 'id', value: role.id }]));
    };

    // Read is the base for everything else on a row: unticking it clears the
    // row's other actions and its sub-actions, and ticking any other action
    // (or a sub-action) ticks Read too - a role can't update what it can't see.
    const togglePerm = (resource: string, action: typeof ACTIONS[number]) => {
        // Roles are User access control - read-only without it.
        if (!hasUserAcl) { promptUserAcl(); return; }
        if (!canUpdateSecurity || selectedRole?.isSystem) return;
        const parent = ALL_ITEMS.find(i => i.key === resource || i.subActions?.some(sa => sa.key === resource));
        const isSubAction = !!parent && parent.key !== resource;
        setPendingPerms(prev => {
            const current = prev.find(p => p.resource === resource);
            const turningOn = !current?.[action];
            const childKeys = new Set(parent && !isSubAction ? (parent.subActions ?? []).map(sa => sa.key) : []);
            return prev.map(p => {
                if (p.resource === resource) {
                    if (action === 'canRead' && !turningOn) {
                        return { ...p, canRead: false, canCreate: false, canUpdate: false, canDelete: false };
                    }
                    return { ...p, [action]: turningOn, ...(turningOn && action !== 'canRead' ? { canRead: true } : {}) };
                }
                // Read off on a row clears its sub-actions as well.
                if (action === 'canRead' && !turningOn && childKeys.has(p.resource)) {
                    return { ...p, canRead: false, canCreate: false, canUpdate: false, canDelete: false };
                }
                // A sub-action on needs its row readable.
                if (isSubAction && turningOn && parent && p.resource === parent.key) {
                    return { ...p, canRead: true };
                }
                return p;
            });
        });
    };

    const handleSavePerms = () => {
        // Roles are User access control - read-only without it.
        if (!hasUserAcl) { promptUserAcl(); return; }
        if (!canUpdateSecurity || !selectedRole || selectedRole.isSystem) return;
        setPermsConfig({
            ...buildApiRequestConfig(routes.entityRoles.updatePermissions, [{ param: 'id', value: selectedRole.id }]),
            body: { permissions: pendingPerms },
        });
    };

    const handleAssign = (entityUserId: number, entityRoleId: number | null) => {
        if (!canUpdateSecurity) return;
        setData(prev => prev.map(u => u.id === entityUserId ? { ...u, entityRoleId } : u));
        setAssignConfig({
            ...buildApiRequestConfig(routes.entityRoles.assignRole, []),
            body: { entityUserId, entityRoleId },
        });
    };

    // Single entry point for the unified Role column - "Admin"/"Read Only" are
    // the built-in EntityUser.role toggle, anything else is a real EntityRole.
    // These are two separate fields on the backend (role vs entityRoleId), so
    // this issues both updates together to keep them in sync from one picker.
    const handleRoleSelect = (userId: number, selectedRoleId: number) => {
        // Roles are User access control - read-only without it.
        if (!hasUserAcl) { promptUserAcl(); return; }
        if (!canUpdateSecurity) return;
        // Making someone Admin is admin-only.
        if (selectedRoleId === -1 && !isAdmin) return;
        const isAdminRole = selectedRoleId === -1;
        const newEntityRoleId = isAdminRole || selectedRoleId === -2 ? null : selectedRoleId;

        setData(prev => prev.map(u => u.id === userId
            ? { ...u, role: isAdminRole ? 'Admin' : 'Read Only', entityRoleId: newEntityRoleId }
            : u));

        handleRoleChange(userId, isAdminRole ? 'admin' : 'user');
        if (!isAdminRole) handleAssign(userId, newEntityRoleId);
    };

    const getPerm = (resource: string) =>
        pendingPerms.find(p => p.resource === resource) ?? defaultPerm(resource);

    // Renders one resource row (plus its sub-action rows, if any). `indent` shifts
    // the label over for items nested under a section header (e.g. Settings), so
    // the matrix visually groups the same way the sidebar menu does.
    const renderResourceRow = (item: ResourceItem, indent: boolean) => {
        const { key, label, subActions, hiddenActions, subActionColumn = 'canCreate' } = item;
        const perm = getPerm(key);
        return (
            <React.Fragment key={key}>
                <TableRow hover>
                    <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap', ...(indent ? { pl: 3 } : {}) }}>{label}</TableCell>
                    {ACTIONS.map(action => (
                        <TableCell key={action} align="center">
                            {hiddenActions?.includes(action) ? null : (
                                <Checkbox
                                    size="small"
                                    checked={!!perm[action]}
                                    onChange={() => togglePerm(key, action)}
                                    disabled={!canUpdateSecurity || !!selectedRole?.isSystem}
                                    sx={{ '&.Mui-checked': { color: 'var(--color-primary)' }, p: 0.5 }}
                                />
                            )}
                        </TableCell>
                    ))}
                </TableRow>
                {subActions?.map(({ key: subKey, label: subLabel }, subIndex) => {
                    const subPerm = getPerm(subKey);
                    const isLastSubAction = subIndex === subActions.length - 1;
                    return (
                        <TableRow key={subKey} hover sx={{ background: 'var(--color-container-background)' }}>
                            <TableCell sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', pl: indent ? 4.5 : 3, whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="svg" width={20} height={24} viewBox="0 0 20 24" fill="none" sx={{ flexShrink: 0 }}>
                                        {isLastSubAction ? (
                                            <path d="M10 0 V8 Q10 12 14 12 H18" stroke="#c4cad4" strokeWidth={1.5} strokeLinecap="round" fill="none" />
                                        ) : (
                                            <>
                                                <line x1={10} y1={0} x2={10} y2={24} stroke="#c4cad4" strokeWidth={1.5} strokeLinecap="round" />
                                                <line x1={10} y1={12} x2={18} y2={12} stroke="#c4cad4" strokeWidth={1.5} strokeLinecap="round" />
                                            </>
                                        )}
                                    </Box>
                                    <Typography component="span" sx={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                                        {subLabel}
                                    </Typography>
                                </Box>
                            </TableCell>
                            {/* Read column, then the sub-action's own column. */}
                            {ACTIONS.map(action => (
                                <TableCell key={action} align="center">
                                    {action === subActionColumn && (
                                        <Checkbox
                                            size="small"
                                            checked={!!subPerm[subActionColumn]}
                                            onChange={() => togglePerm(subKey, subActionColumn)}
                                            disabled={!canUpdateSecurity || !!selectedRole?.isSystem}
                                            sx={{ '&.Mui-checked': { color: 'var(--color-primary)' }, p: 0.5 }}
                                            title="Allowed"
                                        />
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    );
                })}
            </React.Fragment>
        );
    };

    const permsDirty = selectedRole
        ? JSON.stringify(pendingPerms) !== JSON.stringify(selectedRole.permissions)
        : false;

    // The single source of assignable roles - system (Admin/Read Only) and
    // custom - shown both here and in the Roles panel below, so a user's Role
    // column always reflects exactly what's defined there.
    const displayRoles = [...SYSTEM_ROLES, ...roles];

    // Same role list, shaped for the Add/Edit User popup's dropdown (which
    // otherwise falls back to a hardcoded Admin/Read Only-only list that never
    // includes custom roles).
    const userRoleOptions = displayRoles.map(r => ({
        value: r.id === -1 ? 'admin' : r.id === -2 ? 'user' : `role:${r.id}`,
        label: r.name,
    }));

    const columns: Column[] = [
        {
            id: 'name',
            label: 'Name',
            size: 150,
            mobilePrimary: true,
            mobileOrder: 1,
            mobileWidth: 100,
            mobileHideLabel: true,
            onSort: () => handleSort('name')
        },
        {
            id: 'role',
            label: 'Role',
            size: 200,
            type: 'custom',
            mobilePrimary: true,
            mobileOrder: 2,
            mobileWidth: 110,
            customElement: (_value, row) => {
                const selectedId = row.role === 'Admin' ? -1 : (row.entityRoleId ?? -2);
                return (
                    <FormControl size="small" fullWidth>
                        <Select
                            value={selectedId}
                            // Changing an Admin's role is admin-only, same as making
                            // one - and nobody changes their own.
                            disabled={!canUpdateSecurity || (row.role === 'Admin' && !isAdmin) || isSelf(row)}
                            onChange={(e) => handleRoleSelect(row.id, Number(e.target.value))}
                            sx={{ fontSize: 13 }}
                        >
                            {displayRoles.map(r => (
                                <MenuItem key={r.id} value={r.id} disabled={r.id === -1 && !isAdmin}>{r.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            id: 'invitedEmail',
            label: 'Email',
            size: 200,
        },
        {
            id: 'status',
            label: 'Status',
            size: 100,
            type: 'custom',
            mobilePrimary: true,
            mobileOrder: 3,
            mobileWidth: 90,
            customElement: (value) => {
                if (value === 'Pending Invitation') {
                    return (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%'
                        }}>
                            <div style={{
                                color: 'var(--color-primary)',
                                fontWeight: 500,
                                padding: '4px 12px',
                                borderRadius: '4px',
                                border: '1px solid var(--color-primary)'
                            }}>
                                {value}
                            </div>
                        </div>
                    );
                }
                return <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{value}</div>;
            },
            onSort: () => handleSort('status')
        },
        {
            id: 'actions',
            label: 'Actions',
            size: 50,
            type: 'actions',
            onEdit: (row) => { setSelectedUserId(row.id); setOpenModal('edit'); },
            canEdit: () => canUpdateSecurity,
            onDelete: (row) => {setSelectedUserId(row.id); setOpenModal('delete')},
        },
    ];

    const editingUser = data.find(u => u.id === selectedUserId) ?? null;
    return (
        <div style={{ width: '100%' }}>
            <AddUserPopup
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => setOpenModal('')}
                onCreate={openModal === 'add' ? handleCreate : handleUpdate}
                roleOptions={userRoleOptions}
                initialData={openModal === 'edit' && editingUser ? {
                    name: editingUser.name,
                    role: editingUser.entityRoleId
                        ? `role:${editingUser.entityRoleId}`
                        : (editingUser.role === 'Admin' ? 'admin' : 'user'),
                    email: editingUser.invitedEmail,
                    isPending: editingUser.status === 'Pending Invitation',
                } : null}
                roleLocked={openModal === 'edit' && !!editingUser && isSelf(editingUser)}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Account?"
                description="This action will permanently remove the selected account."
                image={{ src: securityImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={handleDelete}
            />
            <DeleteModal
                open={openModal === 'access'}
                message="Role Change Confirmation"
                description={`Are you sure you want to change this user's role to ${data.find(u => u.id === selectedUserId)?.role === 'admin' ? 'User' : 'Admin'}?`}
                image={{ src: accessImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    setOpenModal('');
                }}
            />
            <Topbar
                title={<ListTitle title="User Security" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setSidebarOpen(true)}
                            sx={{
                                color: '#fff',
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)',
                                '&:hover': { backgroundColor: 'var(--color-primary)' }
                            }}
                        >
                            <Menu size={20} color="#fff" />
                        </Button>
                    )
                }
            >
                {!isMobile && (
                    <AddBtn disabled={!canCreateSecurity} onClick={() => setOpenModal('add')} label="Add User" />
                )}
            </Topbar>
            <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={list.filtered}
                    isLoading={fetch.isLoading}
                    sortState={{ column: sortBy, direction: sortDirection }}
                    scrollMode="page"
                    canEditRows={canUpdateSecurity}
                    canDeleteRows={canDeleteSecurity}
                />

                {/* ── Roles & Permissions ─────────────────────────────────────── */}
                <SectionWrapper title="Roles">
                    {!hasUserAcl && <PlanUpgradeBar onUpgrade={promptUserAcl} sx={{ mt: 3 }} />}
                    <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>

                        {/* Role list */}
                        <Box sx={{ width: 220, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" fontWeight={600}>Roles</Typography>
                                <Button size="small" startIcon={<AddIcon />} disabled={!canCreateSecurity} onClick={() => (hasUserAcl ? setAddingRole(true) : promptUserAcl())}>
                                    Add
                                </Button>
                            </Box>

                            {addingRole && canCreateSecurity && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField
                                        size="small"
                                        placeholder="Role name"
                                        value={newRoleName}
                                        onChange={e => setNewRoleName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleCreateRole();
                                            if (e.key === 'Escape') { setAddingRole(false); setNewRoleName(''); }
                                        }}
                                        autoFocus
                                        sx={{ flex: 1 }}
                                    />
                                    <Button size="small" variant="contained" onClick={handleCreateRole}
                                        sx={{ background: 'var(--color-primary)', '&:hover': { background: 'var(--color-primary-hover)' } }}>
                                        Save
                                    </Button>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {displayRoles.map(role => (
                                    <Paper
                                        key={role.id}
                                        onClick={() => pickRole(role)}
                                        elevation={selectedRole?.id === role.id ? 2 : 0}
                                        sx={{
                                            p: 1.5,
                                            cursor: 'pointer',
                                            border: selectedRole?.id === role.id ? '1px solid var(--color-primary)' : '1px solid #eee',
                                            background: selectedRole?.id === role.id ? 'rgba(var(--color-primary-rgb), 0.08)' : 'var(--color-page-background)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography fontSize={13} fontWeight={500} noWrap>{role.name}</Typography>
                                            {role.isSystem && (
                                                <Typography fontSize={11} color="text.secondary">System</Typography>
                                            )}
                                        </Box>
                                        {!role.isSystem && (
                                            <IconButton
                                                size="small"
                                                disabled={!canDeleteSecurity}
                                                onClick={e => { e.stopPropagation(); handleDeleteRole(role); }}
                                                title="Delete role"
                                            >
                                                <DeleteIcon sx={{ fontSize: 16, color: !canDeleteSecurity ? undefined : '#e53e3e' }} />
                                            </IconButton>
                                        )}
                                    </Paper>
                                ))}
                                {!rolesResult.data && !rolesResult.error && (
                                    <Typography fontSize={13} color="text.secondary">Loading...</Typography>
                                )}
                                {rolesResult.data && roles.length === 0 && (
                                    <Typography fontSize={13} color="text.secondary">
                                        No custom roles yet. Add one to get started.
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Permission matrix */}
                        {selectedRole ? (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {selectedRole.name}
                                        </Typography>
                                        {selectedRole.isSystem && (
                                            <Chip label="System" size="small" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', fontSize: 11 }} />
                                        )}
                                    </Box>
                                    {!selectedRole.isSystem && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={!permsDirty || !canUpdateSecurity}
                                            onClick={handleSavePerms}
                                            sx={{ background: 'var(--color-primary)', '&:hover': { background: 'var(--color-primary-hover)' } }}
                                        >
                                            Save Permissions
                                        </Button>
                                    )}
                                </Box>

                                {/* The Resource column plus one column per action (4-6 of
                                    them) is wider than a phone screen - scroll horizontally
                                    within this box rather than squeezing every column or
                                    letting it force the whole page to scroll sideways.
                                    Phones only: any overflow other than visible makes this
                                    box the sticky header's scroll container, so on desktop
                                    the header stuck 65px down *inside* the box - on top of
                                    the rows - instead of to the page. */}
                                <Box sx={{ overflowX: { xs: 'auto', sm: 'visible' } }}>
                                <Table size="small" sx={{ minWidth: 480 }}>
                                    {/* Sticky relative to the real page scroll (same technique as
                                        Topbar) - this section has no bounded/scrolling box of its
                                        own, it just flows with the page, so this is what actually
                                        keeps it visible while scrolling through a long permission list. */}
                                    <TableHead sx={{ position: { xs: 'static', sm: 'sticky' }, top: 65, zIndex: 3 }}>
                                        <TableRow sx={{ background: 'var(--color-container-background)' }}>
                                            <TableCell sx={{ fontWeight: 600, width: 160 }}>Resource</TableCell>
                                            {ACTIONS.map(a => (
                                                <TableCell key={a} align="center" sx={{ fontWeight: 600 }}>
                                                    {ACTION_LABELS[a]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {RESOURCES.map((entry) => {
                                            if (isSection(entry)) {
                                                return (
                                                    <React.Fragment key={entry.section}>
                                                        <TableRow>
                                                            <TableCell colSpan={1 + ACTIONS.length} sx={{ background: 'var(--color-container-background)', borderBottom: 'none', py: 0.75 }}>
                                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                                    {entry.section}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                        {entry.items.map(item => renderResourceRow(item, true))}
                                                    </React.Fragment>
                                                );
                                            }
                                            return renderResourceRow(entry, false);
                                        })}
                                    </TableBody>
                                </Table>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography color="text.secondary" fontSize={14}>
                                    Select a role to edit its permissions
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </SectionWrapper>
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreateSecurity && (
                <Fab
                    onClick={() => setOpenModal('add')}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default Security;
